import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, model, output } from '@angular/core';
import { LucideAngularModule, Star, X, ExternalLink, FileText, Shield, Building2, Calendar, RefreshCw, LUCIDE_ICONS, LucideIconProvider } from 'lucide-angular';
import { MarkdownComponent } from 'ngx-markdown';
import type { RegulatoryUpdate, SignPost } from '@core/models';
import { formatDate, impactChipClass } from '@shared/utils/utils';
import { JurisdictionFlagComponent } from '../jurisdiction-flag/jurisdiction-flag.component';
import { LoadingSpinnerComponent } from '../ui/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-regulatory-update-dialog',
  imports: [LucideAngularModule, MarkdownComponent, JurisdictionFlagComponent, LoadingSpinnerComponent],
  providers: [
    { provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider({ Star, X, ExternalLink, FileText, Shield, Building2, Calendar, RefreshCw }) },
  ],
  templateUrl: './regulatory-update-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegulatoryUpdateDialogComponent {
  update = input.required<RegulatoryUpdate>();
  isOpen = model(false);
  isDetailLoading = input(false);
  toggleFavourite = output<string>();

  protected readonly titleId = `reg-dialog-${crypto.randomUUID()}`;

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    });
    this.destroyRef.onDestroy(() => document.body.classList.remove('overflow-hidden'));
  }

  protected readonly pubDate = computed(() => formatDate(this.update().publicationDate));
  protected readonly updatedDate = computed(() => {
    const d = this.update().updatedAt;
    return d ? formatDate(d) : null;
  });

  protected readonly ingestedDate = computed(() => {
    const d = this.update().crawledAt;
    return d ? formatDate(d) : null;
  });

  protected readonly hasEvidence = computed(() => {
    const posts = this.update().signPosts;
    return posts != null && posts.length > 0;
  });

  protected readonly pageGroups = computed((): { page: number; posts: SignPost[] }[] => {
    const posts = this.update().signPosts ?? [];
    const map = new Map<number, SignPost[]>();
    for (const post of posts) {
      const existing = map.get(post.pageNumber);
      if (existing) {
        existing.push(post);
      } else {
        map.set(post.pageNumber, [post]);
      }
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([page, pagePosts]) => ({ page, posts: pagePosts }));
  });

  protected readonly impactBadgeClass = computed(() => impactChipClass(this.update().impactLevel));

  protected close(): void {
    this.isOpen.set(false);
  }
}
